/**
 * FogBugz Formulator error codes (epic PI-SPEC-SW-E309, ER-1665 Package C,
 * contract fogbugz-formulator 1).
 *
 * Two lists:
 *
 * - {@link FOGBUGZ_FORMULATOR_API_ERROR_CODES}: what the SpecTree API refuses
 *   a FogBugz Formulator route with (the `code` of the error body).
 * - {@link FOGBUGZ_FORMULATOR_ENGINE_ERROR_CODES}: what the SpecTree Toolbox
 *   fogbugz-formulator engine resolves a bridge call with, or reports on a
 *   `turn-failed` event. It starts with every fogbugz engine code
 *   (`FOGBUGZ_ENGINE_ERROR_CODES`: Claude not found or signed out, API-key
 *   billing refused, usage limit, `TIMED_OUT`, ...), imported rather than
 *   re-typed, and passes the API codes through.
 *
 * A failed turn posts nothing to SpecTree, so the person's typed answer is
 * kept and can be sent again. Pages show {@link fogbugzFormulatorErrorMessage},
 * never the code.
 */
import { FOGBUGZ_ENGINE_ERROR_CODES } from "../fogbugz/schemas.js";
import { FOGBUGZ_KEY_REQUIRED, FOGBUGZ_KEY_REQUIRED_MESSAGE } from "../fogbugz/reads.js";
import { FOGBUGZ_FORMULATOR_EVTX_MAX_BYTES, FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS, FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS, FOGBUGZ_FORMULATOR_SCREENSHOT_MAX_BYTES, } from "./constants.js";
// ---------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------
/** The chosen FogBugz project has no FogBugz ↔ ADO project link (epic PI-SPEC-SW-E308). */
export const FOGBUGZ_PROJECT_NOT_LINKED = "FOGBUGZ_PROJECT_NOT_LINKED";
/** The linked ADO project has no repositories mapped, so there is no code to read. */
export const FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED = "REPO_NOT_MAPPED";
/**
 * The linked ADO project's repository copies are missing on this computer:
 * Setup has not copied them, or the person holds no role on that ADO project
 * (only a non-Viewer role there gets the copies).
 */
export const FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP = "REPO_NOT_SET_UP";
/**
 * 400 at submit: a bug case is below the Bug bar (full Intent v1 coverage
 * plus the ADO grooming bar for a Bug). Nothing is filed; the error details
 * carry the `fogbugzBugBarResultSchema` result naming each missing rule.
 */
export const BELOW_BUG_BAR = "BELOW_BUG_BAR";
/** 404: the session is unknown, belongs to someone else, or has expired. */
export const FOGBUGZ_FORMULATOR_SESSION_NOT_FOUND = "INTERVIEW_SESSION_NOT_FOUND";
/** 409: the session changed since the caller read it (another turn or a second window). */
export const FOGBUGZ_FORMULATOR_SESSION_CONFLICT = "INTERVIEW_SESSION_CONFLICT";
/**
 * 502 at submit: FogBugz did not confirm the case (no reply), so it may have
 * been filed. The session stays claimed; submitting again searches FogBugz for
 * the case first and files only when it is not there.
 */
export const FOGBUGZ_SUBMIT_UNCERTAIN = "FOGBUGZ_SUBMIT_UNCERTAIN";
/** Every code a FogBugz Formulator API route refuses with. */
export const FOGBUGZ_FORMULATOR_API_ERROR_CODES = [
    FOGBUGZ_PROJECT_NOT_LINKED,
    FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED,
    BELOW_BUG_BAR,
    FOGBUGZ_KEY_REQUIRED,
    FOGBUGZ_FORMULATOR_SESSION_NOT_FOUND,
    FOGBUGZ_FORMULATOR_SESSION_CONFLICT,
    FOGBUGZ_SUBMIT_UNCERTAIN,
];
/** True when `value` is a FogBugz Formulator API error code. */
export function isFogbugzFormulatorApiErrorCode(value) {
    return typeof value === "string" && FOGBUGZ_FORMULATOR_API_ERROR_CODES.includes(value);
}
/**
 * Every code the fogbugz-formulator engine reports: the fogbugz toolset's
 * engine codes, the API codes it passes through, and its own.
 */
export const FOGBUGZ_FORMULATOR_ENGINE_ERROR_CODES = [
    ...FOGBUGZ_ENGINE_ERROR_CODES,
    ...FOGBUGZ_FORMULATOR_API_ERROR_CODES,
    FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP,
    /** Claude finished without a valid `SPECTREE_FOGBUGZ_FORMULATOR_TURN` line for this session. */
    "NO_TURN_RETURNED",
    /** A screenshot is above `FOGBUGZ_FORMULATOR_SCREENSHOT_MAX_BYTES` or not an accepted image type. */
    "IMAGE_TOO_LARGE",
    /** More than `FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS` screenshots. */
    "TOO_MANY_IMAGES",
    /** An event log is above `FOGBUGZ_FORMULATOR_EVTX_MAX_BYTES`, or not a `.evtx` file. */
    "EVENT_LOG_INVALID",
    /** More than `FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS` event logs. */
    "TOO_MANY_EVENT_LOGS",
    /** The named attachment is not in the session's folder on this computer (added on another computer, or forgotten). */
    "ATTACHMENT_NOT_FOUND",
];
/** True when `value` is a fogbugz-formulator engine error code. */
export function isFogbugzFormulatorEngineErrorCode(value) {
    return typeof value === "string" && FOGBUGZ_FORMULATOR_ENGINE_ERROR_CODES.includes(value);
}
// ---------------------------------------------------------------------------
// Plain messages
// ---------------------------------------------------------------------------
/** The plain sentence for {@link FOGBUGZ_PROJECT_NOT_LINKED}. */
export const FOGBUGZ_PROJECT_NOT_LINKED_MESSAGE = "This FogBugz project is not linked to a product yet. Ask an Admin, or the Project Manager of the product's ADO project, to link it on the Toolbox Project page, then try again.";
/** The plain sentence for `REPO_NOT_MAPPED`, naming the linked ADO project. */
export function fogbugzFormulatorRepoNotMappedMessage(adoProject) {
    return `The ADO project ${adoProject}, linked to this FogBugz project, has no code repositories set up. Ask an Admin to set its repositories on the Toolbox Repos page, then try again.`;
}
/** The plain sentence for `REPO_NOT_SET_UP`, naming the linked ADO project. */
export function fogbugzFormulatorRepoNotSetUpMessage(adoProject) {
    return `The code of ${adoProject} is not on this computer yet. Run Setup to get the project code. If you have no role on the ADO project ${adoProject}, ask its Project Manager to add you, then run Setup.`;
}
/** The plain sentence for {@link BELOW_BUG_BAR}; the review step lists each missing rule under it. */
export const BELOW_BUG_BAR_MESSAGE = "This bug report is not complete enough to file yet. Fill in what is listed below, then submit again.";
/**
 * The plain sentence for every code a page may show. `adoProject` names the
 * linked ADO project in the repository messages; without it they say "the
 * linked ADO project". Codes with no message of their own fall back to
 * `fallback` (the engine's own sentence) or a generic sentence.
 */
export function fogbugzFormulatorErrorMessage(code, options = {}) {
    const adoProject = options.adoProject ?? "the linked ADO project";
    switch (code) {
        case FOGBUGZ_PROJECT_NOT_LINKED:
            return FOGBUGZ_PROJECT_NOT_LINKED_MESSAGE;
        case FOGBUGZ_FORMULATOR_REPO_NOT_MAPPED:
            return fogbugzFormulatorRepoNotMappedMessage(adoProject);
        case FOGBUGZ_FORMULATOR_REPO_NOT_SET_UP:
            return fogbugzFormulatorRepoNotSetUpMessage(adoProject);
        case BELOW_BUG_BAR:
            return BELOW_BUG_BAR_MESSAGE;
        case FOGBUGZ_KEY_REQUIRED:
            return FOGBUGZ_KEY_REQUIRED_MESSAGE;
        case FOGBUGZ_FORMULATOR_SESSION_NOT_FOUND:
            return "This interview has ended or expired. Start a new one.";
        case FOGBUGZ_FORMULATOR_SESSION_CONFLICT:
            return "This interview changed in another window. Reload to see the latest, then try again.";
        case FOGBUGZ_SUBMIT_UNCERTAIN:
            return "FogBugz did not confirm the case, so it may have been filed. Submit again: SpecTree checks FogBugz for it first and files it only if it is not there.";
        case "TIMED_OUT":
            return "Claude took too long on this step. Your answer is kept; try again.";
        case "CLAUDE_NOT_SIGNED_IN":
            return "Sign in to Claude in Setup, then try again.";
        case "CLAUDE_NOT_FOUND":
            return "Claude Code is not installed on this computer. Install it in Setup, then try again.";
        case "API_KEY_BILLING":
            return "Claude Code would bill an API key instead of your Claude subscription. Sign in with your subscription in Setup, then try again.";
        case "BUSY":
            return "Claude is already working on another FogBugz step. Wait for it to finish, then try again.";
        case "IMAGE_TOO_LARGE":
            return `Screenshots must be images of at most ${String(FOGBUGZ_FORMULATOR_SCREENSHOT_MAX_BYTES / (1024 * 1024))} MB.`;
        case "TOO_MANY_IMAGES":
            return `You can add at most ${String(FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS)} screenshots.`;
        case "EVENT_LOG_INVALID":
            return `Event logs must be .evtx files of at most ${String(FOGBUGZ_FORMULATOR_EVTX_MAX_BYTES / (1024 * 1024))} MB.`;
        case "TOO_MANY_EVENT_LOGS":
            return `You can add at most ${String(FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS)} event logs.`;
        case "ATTACHMENT_NOT_FOUND":
            return "That file is not on this computer any more. Add it again.";
        default:
            return options.fallback ?? "Something went wrong. Try again.";
    }
}
//# sourceMappingURL=errors.js.map